import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../api';

const VALID_SECTS = ['theravada', 'mahayana', 'vajrayana', 'zen', 'all-sects'];
const DIFFICULTY_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

const EMPTY_FORM = {
    title: '',
    sanskrit_title: '',
    mantra_text: '',
    mantra_text_sanskrit: '',
    pronunciation_guide: '',
    translation: '',
    meaning: '',
    tradition: '',
    deity_association: '',
    category: '',
    difficulty_level: '',
    recommended_repetitions: '',
    practice_instructions: '',
    historical_context: '',
    sects: [],
    featured: false,
    public: true,
    tags: '',
    audio_link: '',
    video_link: '',
};

const getToken = async () => {
    const { tokens } = await fetchAuthSession();
    return tokens.idToken.toString();
};

const AdminContent = () => {
    const { user, isAuthenticated } = useAuth();
    const [view, setView] = useState('list');
    const [mantras, setMantras] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedMantra, setSelectedMantra] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [uploadingAudio, setUploadingAudio] = useState(false);

    useEffect(() => {
        document.body.style.backgroundImage = 'url("/images/adminPageBkg.jpg")';
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundAttachment = 'fixed';
        return () => {
            document.body.style.backgroundImage = 'none';
            document.body.style.backgroundSize = '';
            document.body.style.backgroundPosition = '';
            document.body.style.backgroundAttachment = '';
        };
    }, []);

    const loadMantras = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/mantras?limit=100`);
            setMantras(res.data?.mantras || []);
        } catch (err) {
            setError('Failed to load mantras.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated && user?.isAdmin) {
            loadMantras();
        }
    }, [isAuthenticated, user, loadMantras]);

    if (!isAuthenticated) return <Navigate to="/auth" replace />;

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-10 text-center max-w-sm">
                    <p className="text-white text-lg mb-4">Administrator access required.</p>
                    <Link to="/" className="text-amber-400 hover:underline text-sm">Return home</Link>
                </div>
            </div>
        );
    }

    const openAdd = () => {
        setSelectedMantra(null);
        setForm(EMPTY_FORM);
        setError('');
        setSuccess('');
        setView('form');
    };

    const openEdit = (mantra) => {
        setSelectedMantra(mantra);
        setForm({
            title: mantra.title || '',
            sanskrit_title: mantra.sanskrit_title || '',
            mantra_text: mantra.mantra_text || '',
            mantra_text_sanskrit: mantra.mantra_text_sanskrit || '',
            pronunciation_guide: mantra.pronunciation_guide || '',
            translation: mantra.translation || '',
            meaning: mantra.meaning || '',
            tradition: mantra.tradition || '',
            deity_association: mantra.deity_association || '',
            category: mantra.category || '',
            difficulty_level: mantra.difficulty_level || '',
            recommended_repetitions: mantra.recommended_repetitions?.toString() || '',
            practice_instructions: mantra.practice_instructions || '',
            historical_context: mantra.historical_context || '',
            sects: Array.isArray(mantra.sects) ? mantra.sects : [],
            featured: mantra.featured === 'true',
            public: mantra.public !== false,
            tags: Array.isArray(mantra.tags) ? mantra.tags.join(', ') : (mantra.tags || ''),
            audio_link: mantra.audio_link || '',
            video_link: mantra.video_link || '',
        });
        setError('');
        setSuccess('');
        setView('form');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSectToggle = (sect) => {
        setForm(prev => ({
            ...prev,
            sects: prev.sects.includes(sect)
                ? prev.sects.filter(s => s !== sect)
                : [...prev.sects, sect],
        }));
    };

    const handleDelete = async (mantra) => {
        if (!window.confirm(`Delete "${mantra.title}"? This cannot be undone.`)) return;
        try {
            const token = await getToken();
            await axios.delete(`${API_URL}/admin/mantras/${mantra.uuid}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMantras(prev => prev.filter(m => m.uuid !== mantra.uuid));
        } catch (err) {
            setError('Delete failed. Please try again.');
        }
    };

    const handleAudioUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingAudio(true);
        setError('');
        try {
            const token = await getToken();
            const safeFilename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const metaRes = await fetch(`${API_URL}/admin/upload-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ filename: safeFilename, contentType: 'audio/mpeg', folder: 'mantras/' }),
            });
            const { uploadUrl, assetUrl } = await metaRes.json();
            await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': 'audio/mpeg' } });
            setForm(prev => ({ ...prev, audio_link: assetUrl }));
        } catch (err) {
            setError('Audio upload failed. Please try again.');
        } finally {
            setUploadingAudio(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!form.title.trim()) { setError('Title is required.'); return; }
        if (!form.mantra_text.trim()) { setError('Mantra text is required.'); return; }

        setSaving(true);
        try {
            const token = await getToken();
            const payload = {
                ...form,
                featured: form.featured ? 'true' : 'false',
                recommended_repetitions: form.recommended_repetitions ? Number(form.recommended_repetitions) : null,
                tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
            };

            if (selectedMantra) {
                const res = await axios.put(`${API_URL}/admin/mantras/${selectedMantra.uuid}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setMantras(prev => prev.map(m => m.uuid === selectedMantra.uuid ? res.data.mantra : m));
                setSuccess('Mantra updated.');
            } else {
                const res = await axios.post(`${API_URL}/admin/mantras`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setMantras(prev => [...prev, res.data.mantra]);
                setSuccess('Mantra created.');
            }
            setView('list');
        } catch (err) {
            setError('Save failed. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // ── List view ──────────────────────────────────────────────────────────────
    if (view === 'list') {
        return (
            <div className="min-h-screen py-12 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl px-6 py-4">
                            <Link to="/admin" className="text-amber-400 text-sm hover:underline block mb-1">← Admin Portal</Link>
                            <h1 className="text-2xl font-bold text-white">Mantra Content</h1>
                        </div>
                        <button
                            onClick={openAdd}
                            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
                        >
                            + Add Mantra
                        </button>
                    </div>

                    {error && <p className="text-red-400 mb-4">{error}</p>}
                    {success && <p className="text-green-400 mb-4">{success}</p>}

                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl overflow-hidden">
                        {loading ? (
                            <p className="text-gray-400 p-8 text-center">Loading mantras...</p>
                        ) : mantras.length === 0 ? (
                            <p className="text-gray-400 p-8 text-center">No mantras yet. Add one above.</p>
                        ) : (
                            <table className="w-full text-sm text-left">
                                <thead className="text-gray-400 border-b border-slate-700">
                                    <tr>
                                        <th className="px-6 py-3">Title</th>
                                        <th className="px-6 py-3">Sects</th>
                                        <th className="px-6 py-3">Tradition</th>
                                        <th className="px-6 py-3">Featured</th>
                                        <th className="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mantras.map(mantra => (
                                        <tr key={mantra.uuid} className="border-b border-slate-800 hover:bg-slate-800/40">
                                            <td className="px-6 py-4 text-white font-medium">{mantra.title}</td>
                                            <td className="px-6 py-4 text-gray-300">
                                                {mantra.sects?.join(', ') || '—'}
                                            </td>
                                            <td className="px-6 py-4 text-gray-300">{mantra.tradition || '—'}</td>
                                            <td className="px-6 py-4">
                                                {mantra.featured === 'true'
                                                    ? <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full">Featured</span>
                                                    : <span className="text-gray-500">—</span>
                                                }
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => openEdit(mantra)}
                                                    className="text-amber-400 hover:text-amber-300 mr-4 text-xs font-semibold"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(mantra)}
                                                    className="text-red-400 hover:text-red-300 text-xs font-semibold"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Form view ──────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-3xl mx-auto">
                <div className="mb-6">
                    <button
                        onClick={() => setView('list')}
                        className="text-amber-400 hover:underline text-sm"
                    >
                        ← Back to list
                    </button>
                    <h1 className="text-2xl font-bold text-white mt-2">
                        {selectedMantra ? 'Edit Mantra' : 'Add New Mantra'}
                    </h1>
                </div>

                {error && <p className="text-red-400 mb-4">{error}</p>}

                <form onSubmit={handleSubmit} className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-8 space-y-5">
                    {/* Title */}
                    <div>
                        <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
                            Mantra Title <span className="text-red-400">*</span>
                        </label>
                        <input
                            id="title"
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                            required
                        />
                    </div>

                    {/* Sanskrit title */}
                    <div>
                        <label htmlFor="sanskrit_title" className="block text-sm font-medium text-gray-300 mb-1">Sanskrit Title</label>
                        <input id="sanskrit_title" name="sanskrit_title" value={form.sanskrit_title} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Mantra text */}
                    <div>
                        <label htmlFor="mantra_text" className="block text-sm font-medium text-gray-300 mb-1">
                            Mantra Text <span className="text-red-400">*</span>
                        </label>
                        <input id="mantra_text" name="mantra_text" value={form.mantra_text} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                            required />
                    </div>

                    {/* Mantra text Sanskrit */}
                    <div>
                        <label htmlFor="mantra_text_sanskrit" className="block text-sm font-medium text-gray-300 mb-1">Mantra Text (Sanskrit script)</label>
                        <input id="mantra_text_sanskrit" name="mantra_text_sanskrit" value={form.mantra_text_sanskrit} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Pronunciation */}
                    <div>
                        <label htmlFor="pronunciation_guide" className="block text-sm font-medium text-gray-300 mb-1">Pronunciation Guide</label>
                        <input id="pronunciation_guide" name="pronunciation_guide" value={form.pronunciation_guide} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Translation */}
                    <div>
                        <label htmlFor="translation" className="block text-sm font-medium text-gray-300 mb-1">Translation</label>
                        <input id="translation" name="translation" value={form.translation} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Meaning */}
                    <div>
                        <label htmlFor="meaning" className="block text-sm font-medium text-gray-300 mb-1">Meaning</label>
                        <textarea id="meaning" name="meaning" value={form.meaning} onChange={handleChange} rows={3}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Tradition + Category */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="tradition" className="block text-sm font-medium text-gray-300 mb-1">Tradition</label>
                            <input id="tradition" name="tradition" value={form.tradition} onChange={handleChange}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                        </div>
                        <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                            <input id="category" name="category" value={form.category} onChange={handleChange}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                        </div>
                    </div>

                    {/* Difficulty + Repetitions */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="difficulty_level" className="block text-sm font-medium text-gray-300 mb-1">Difficulty</label>
                            <select id="difficulty_level" name="difficulty_level" value={form.difficulty_level} onChange={handleChange}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500">
                                <option value="">— Select —</option>
                                {DIFFICULTY_LEVELS.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="recommended_repetitions" className="block text-sm font-medium text-gray-300 mb-1">Recommended Repetitions</label>
                            <input id="recommended_repetitions" name="recommended_repetitions" type="number" value={form.recommended_repetitions} onChange={handleChange}
                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                        </div>
                    </div>

                    {/* Deity */}
                    <div>
                        <label htmlFor="deity_association" className="block text-sm font-medium text-gray-300 mb-1">Deity Association</label>
                        <input id="deity_association" name="deity_association" value={form.deity_association} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Sects */}
                    <div>
                        <span className="block text-sm font-medium text-gray-300 mb-2">Sects</span>
                        <div className="flex flex-wrap gap-3">
                            {VALID_SECTS.map(sect => (
                                <label key={sect} className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.sects.includes(sect)}
                                        onChange={() => handleSectToggle(sect)}
                                        className="accent-amber-500"
                                    />
                                    <span className="capitalize">{sect}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Practice instructions */}
                    <div>
                        <label htmlFor="practice_instructions" className="block text-sm font-medium text-gray-300 mb-1">Practice Instructions</label>
                        <textarea id="practice_instructions" name="practice_instructions" value={form.practice_instructions} onChange={handleChange} rows={3}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Historical context */}
                    <div>
                        <label htmlFor="historical_context" className="block text-sm font-medium text-gray-300 mb-1">Historical Context</label>
                        <textarea id="historical_context" name="historical_context" value={form.historical_context} onChange={handleChange} rows={3}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Tags */}
                    <div>
                        <label htmlFor="tags" className="block text-sm font-medium text-gray-300 mb-1">Tags (comma-separated)</label>
                        <input id="tags" name="tags" value={form.tags} onChange={handleChange}
                            placeholder="compassion, beginner, purification"
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Audio link + upload */}
                    <div>
                        <label htmlFor="audio_link" className="block text-sm font-medium text-gray-300 mb-1">Audio Link</label>
                        <div className="flex gap-2">
                            <input id="audio_link" name="audio_link" value={form.audio_link} onChange={handleChange}
                                placeholder="https://assets.buddhawisdom.org/mantras/..."
                                className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                            <label className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${uploadingAudio ? 'bg-slate-700 text-gray-500' : 'bg-amber-600 hover:bg-amber-700 text-white'}`}>
                                {uploadingAudio ? 'Uploading...' : 'Upload MP3'}
                                <input type="file" accept="audio/mpeg,.mp3" onChange={handleAudioUpload} className="hidden" disabled={uploadingAudio} />
                            </label>
                        </div>
                    </div>

                    {/* Video link */}
                    <div>
                        <label htmlFor="video_link" className="block text-sm font-medium text-gray-300 mb-1">Video Link</label>
                        <input id="video_link" name="video_link" value={form.video_link} onChange={handleChange}
                            className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-amber-500" />
                    </div>

                    {/* Toggles */}
                    <div className="flex gap-6">
                        <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                            <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="accent-amber-500" />
                            Featured mantra
                        </label>
                        <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                            <input type="checkbox" name="public" checked={form.public} onChange={handleChange} className="accent-amber-500" />
                            Public
                        </label>
                    </div>

                    {/* Submit */}
                    <div className="flex gap-4 pt-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-amber-500 hover:bg-amber-600 disabled:bg-slate-600 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors"
                        >
                            {saving ? 'Saving...' : selectedMantra ? 'Update Mantra' : 'Create Mantra'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setView('list')}
                            className="bg-slate-700 hover:bg-slate-600 text-gray-300 px-6 py-2.5 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminContent;
